/**
 * Flipt 엔진이 요구하는 WebAssembly 능력을 두 시점에서 판정한다.
 *
 * - 실행 전: `supportsWasmReferenceTypes` — externref 문법을 받아들이는가 (validate).
 * - 실행 후: `isWasmRuntimeError` — 엔진이 돌다가 WASM 런타임 오류로 죽었는가.
 *
 * 둘이 나뉜 이유: WebKit 16.3 이하는 문법 검사는 통과시키면서 externref 테이블
 * 접근에서 런타임에 터지고(`Out of bounds table access`), 같은 OS 버전의 일부
 * 기기만 그렇다. 실행해 보기 전에는 알 수 없어 사후 판정이 필요하다.
 */

/**
 * Smallest valid WASM module whose function signature uses `externref`.
 *
 * Flipt's engine requires WebAssembly reference types. Some Chromium-based
 * vendor browsers expose WebAssembly but reject `externref`, so checking only
 * for `window.WebAssembly` still lets initialization throw a CompileError.
 */
const WASM_REFERENCE_TYPES_PROBE = new Uint8Array([
  0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, 0x01, 0x05, 0x01, 0x60, 0x01,
  0x6f, 0x00,
]);

export function supportsWasmReferenceTypes(): boolean {
  return (
    typeof WebAssembly !== 'undefined' &&
    typeof WebAssembly.validate === 'function' &&
    WebAssembly.validate(WASM_REFERENCE_TYPES_PROBE)
  );
}

/**
 * WASM 엔진이 **실행 중에** 던진 실패. 네트워크·HTTP 실패와 달리 재시도해도 매번
 * 같은 자리에서 터지므로 에러가 아니라 "이 브라우저는 안 된다" 로 다룬다.
 *
 * `RuntimeError` 만 본다 — `CompileError` 는 일부러 제외한다. 컴파일 단계의 정당한
 * 비호환(구형 Android vendor 브라우저의 `invalid value type 'externref'`)은
 * `supportsWasmReferenceTypes` 프로브가 실행 전에 이미 걸러낸다. 실제로 프로브가
 * 들어간 릴리스부터 Sentry 의 CompileError 는 0 이 됐다(직전 릴리스 39건 → 0건).
 * 그러므로 **프로브를 통과한 뒤의 CompileError 는 알려진 원인이 없는 상황**
 * (손상된 WASM 청크, 잘못된 SDK 빌드 등)이고, 조용히 삼키면 운영 장애가 debug
 * 로그에만 남는다. 그런 건 기존대로 `onError` 로 보고한다.
 *
 * 메시지 문자열로 좁히지 않는 이유: 30일 실측에서 RuntimeError 는 전부
 * `Out of bounds table access (…engine_snapshot…)` 하나였고, 문구는 엔진마다
 * 달라질 수 있어 타입 판정이 더 안정적이다.
 */
export function isWasmRuntimeError(error: unknown): boolean {
  return (
    typeof WebAssembly !== 'undefined' &&
    error instanceof WebAssembly.RuntimeError
  );
}
